import * as THREE from "three";

// Radial fade in units of the floor's half-extent. 0.55 keeps a ~5.5 m solid
// disk on the default 10×10 studio floor (a large sofa stays fully planted);
// 1.0 is the edge midpoint, so the rectangle silhouette melts into the backdrop.
export const FLOOR_FADE_START = 0.55;
export const FLOOR_FADE_END = 1.0;

export function createFloorFadeUniforms() {
  return {
    uFloorFade: { value: 1 },
    uFloorHalfSize: { value: new THREE.Vector2(5, 5) },
    uFadeStart: { value: FLOOR_FADE_START },
    uFadeEnd: { value: FLOOR_FADE_END },
    uFadeColor: { value: new THREE.Color(1, 1, 1) },
  };
}

/** GLSL smoothstep matching the floor-edge shader, for tests and any CPU preview. */
export function floorEdgeFade(x, z, halfWidth, halfDepth, start = FLOOR_FADE_START, end = FLOOR_FADE_END) {
  const r = Math.hypot(x / Math.max(halfWidth, 1e-4), z / Math.max(halfDepth, 1e-4));
  if (r <= start) return 1;
  if (r >= end) return 0;
  const t = (r - start) / (end - start);
  return 1 - (t * t * (3 - 2 * t));
}

/**
 * The path tracer can't run the fade shader, so it gets the same mask as an alphaMap
 * over the plane's 0..1 UVs — elliptical on a non-square room, like the shader. The
 * tracer reads alpha from G; every channel carries it.
 */
export function createFloorFadeAlphaMap(size = 256) {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const a = Math.round(floorEdgeFade(((x + 0.5) / size) * 2 - 1, ((y + 0.5) / size) * 2 - 1, 1, 1) * 255);
      data.fill(a, (y * size + x) * 4, (y * size + x) * 4 + 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

export function environmentWantsFloorFade(environment) {
  return environment?.visibility?.walls === false && environment?.visibility?.floor !== false;
}

/**
 * Mix the lit floor (or contact-shadow) colour toward the studio backdrop so the
 * plane's rectangle disappears. Shared uniform objects, so one update drives every
 * dressed material.
 */
export function applyFloorEdgeFade(material, uniforms) {
  if (!material || material.userData.floorEdgeFade) return material;
  material.userData.floorEdgeFade = true;

  const prevKey = material.customProgramCacheKey;
  material.customProgramCacheKey = () =>
    `${typeof prevKey === "function" ? prevKey.call(material) : ""}|floor-edge-fade`;

  const prevCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    prevCompile?.(shader, renderer);
    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vFloorWorldPos;")
      .replace(
        "#include <project_vertex>",
        "#include <project_vertex>\nvFloorWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;",
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
varying vec3 vFloorWorldPos;
uniform float uFloorFade;
uniform vec2 uFloorHalfSize;
uniform float uFadeStart;
uniform float uFadeEnd;
uniform vec3 uFadeColor;`,
      )
      .replace(
        "#include <opaque_fragment>",
        `#include <opaque_fragment>
{
  vec2 p = vFloorWorldPos.xz / max(uFloorHalfSize, vec2(1.0e-4));
  float edge = 1.0 - smoothstep(uFadeStart, uFadeEnd, length(p));
  float fade = mix(1.0, edge, uFloorFade);
  gl_FragColor.rgb = mix(uFadeColor, gl_FragColor.rgb, fade);
}`,
      );
  };
  material.needsUpdate = true;
  return material;
}

/** Multiply the reflector's existing footprint alpha by the same radial mask. */
export function applyReflectorEdgeFade(material, uniforms) {
  if (!material?.uniforms || material.userData.floorEdgeFade) return material;
  material.userData.floorEdgeFade = true;

  Object.assign(material.uniforms, uniforms);

  const decls =
    "uniform float uFloorFade;\n\t\tuniform vec2 uFloorHalfSize;\n\t\tuniform float uFadeStart;\n\t\tuniform float uFadeEnd;";
  if (!material.fragmentShader.includes("uFloorFade")) {
    material.fragmentShader = material.fragmentShader.replace(
      "uniform float fadeEnd;",
      `uniform float fadeEnd;\n\t\t${decls}`,
    );
  }
  if (!material.fragmentShader.includes("floorEdge")) {
    material.fragmentShader = material.fragmentShader.replace(
      "float reflFade = reflectStrength * ( 1.0 - smoothstep( fadeStart, fadeEnd, fpDist ) );",
      `vec2 fadeP = vWorldPosition.xz / max(uFloorHalfSize, vec2(1.0e-4));\n\t\t\tfloat floorEdge = mix(1.0, 1.0 - smoothstep(uFadeStart, uFadeEnd, length(fadeP)), uFloorFade);\n\t\t\tfloat reflFade = reflectStrength * ( 1.0 - smoothstep( fadeStart, fadeEnd, fpDist ) ) * floorEdge;`,
    );
  }
  material.needsUpdate = true;
  return material;
}
